import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
    PieChart, Pie, Cell, BarChart, Bar
} from 'recharts';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './Admin.css';

const PAGE_SIZE = 20;

/* ---- Custom Tooltip ---- */
const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
        <div className="chart-tooltip">
            {label && <div className="chart-tooltip-label">{label}</div>}
            {payload.map((p, i) => (
                <div key={i} style={{ color: p.color, fontWeight: 700 }}>
                    {p.name}: <span className="chart-tooltip-value">{p.value}</span>
                </div>
            ))}
        </div>
    );
};

/* ---- KPI Card ---- */
const KPICard = ({ label, value, icon, accentColor, iconBg, sub }) => (
    <div className="stat-card" style={{ '--card-accent': accentColor, '--card-icon-bg': iconBg }}>
        <div className="stat-card-icon">{icon}</div>
        <span className="stat-label">{label}</span>
        <strong>{value}</strong>
        {sub && <span className="stat-card-sub">{sub}</span>}
    </div>
);

function Admin() {
    const location = useLocation();
    const navigate = useNavigate();
    const initialEmployee = location.state?.employee || null;

    const [adminIdentity, setAdminIdentity] = useState(() => {
        if (initialEmployee) return initialEmployee.identity_number;
        try {
            const emp = JSON.parse(sessionStorage.getItem('employee'));
            if (emp && emp.role === 'admin') return emp.identity_number;
        } catch (e) { }
        return null;
    });
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState('');
    const [exactDate, setExactDate] = useState('');
    const [selectedUsers, setSelectedUsers] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState(() => {
        return sessionStorage.getItem('adminView') || 'analytics';
    });
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [compactRows, setCompactRows] = useState(false);
    const [message, setMessage] = useState('');
    const [deleteModal, setDeleteModal] = useState(null); // { identityNumber, name }
    const didInitLiveSearch = useRef(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalUsers, setTotalUsers] = useState(0);
    const [summary, setSummary] = useState({
        admins: 0, employees: 0, employeesFilled: 0, employeesPending: 0
    });
    const [filterStatus, setFilterStatus] = useState('all');

    const [analytics, setAnalytics] = useState({
        summary: {
            employeesTotal: 0, adminsTotal: 0, employeesFilled: 0,
            employeesPending: 0, totalDependents: 0, completionRate: 0,
            avgDependentsPerFilled: 0, avgAge: 0
        },
        topLocations: [],
        monthlyTrends: [],
        distributions: { agency: [], jobTitle: [], administration: [], gender: [], city: [] }
    });
    const [analyticsLoading, setAnalyticsLoading] = useState(false);

    const [newUser, setNewUser] = useState({
        identity_number: '', full_name: '', phone_number: '', location: '', role: 'employee'
    });

    /* ---------- Init ---------- */
    // Redirect to login if not authenticated
    useEffect(() => {
        if (!adminIdentity) {
            navigate('/');
        }
    }, [adminIdentity, navigate]);

    useEffect(() => {
        let identity = adminIdentity;
        if (!identity) {
            try {
                const stored = sessionStorage.getItem('employee');
                if (stored) {
                    const emp = JSON.parse(stored);
                    setAdminIdentity(emp.identity_number);
                    identity = emp.identity_number;
                }
            } catch (_e) { /* ignore */ }
        }
        if (!identity) { navigate('/'); return; }
        if (view === 'analytics') {
            fetchAnalytics();
        } else {
            fetchUsers({ page: 1, search: '', date: '' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminIdentity, view]);

    /* ---------- Fetch users ---------- */
    const fetchUsers = useCallback(async ({ search = q, date = exactDate, status = filterStatus, page: nextPage = page } = {}) => {
        setLoading(true);
        setMessage('');
        try {
            const params = new URLSearchParams({ page: String(nextPage), limit: String(PAGE_SIZE) });
            if (search && search.trim()) params.set('search', search.trim());
            if (date) params.set('exactDate', date);
            if (status && status !== 'all') params.set('status', status);


            const resp = await fetch(`${API_ENDPOINTS.ADMIN_USERS}?${params}`, {
                headers: { 'Content-Type': 'application/json', 'x-employee-identity': adminIdentity }
            });
            if (!resp.ok) { const e = await resp.json().catch(() => ({})); alert(e.error || 'فشل جلب المستخدمين'); return; }
            const data = await resp.json();
            const nextUsers = data.results || [];
            const resolvedTotal = Number.isFinite(Number(data.total)) ? Number(data.total) : nextUsers.length;
            const resolvedPage = data.page || nextPage;
            const resolvedTotalPages = data.totalPages || Math.max(1, Math.ceil((resolvedTotal || 1) / PAGE_SIZE));

            setUsers(nextUsers);
            setPage(resolvedPage);
            setTotalPages(resolvedTotalPages);
            setTotalUsers(resolvedTotal);
            setSummary({
                admins: Number(data.summary?.admins ?? nextUsers.filter(u => u.role === 'admin').length),
                employees: Number(data.summary?.employees ?? nextUsers.filter(u => u.role === 'employee').length),
                employeesFilled: Number(data.summary?.employeesFilled ?? 0),
                employeesPending: Number(data.summary?.employeesPending ?? 0)
            });
        } catch { alert('فشل الاتصال بالخادم'); }
        finally { setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminIdentity]);

    /* ---------- Fetch analytics ---------- */
    const fetchAnalytics = useCallback(async () => {
        setAnalyticsLoading(true);
        try {
            const resp = await fetch(API_ENDPOINTS.ADMIN_ANALYTICS, {
                headers: { 'Content-Type': 'application/json', 'x-employee-identity': adminIdentity }
            });
            if (!resp.ok) { const e = await resp.json().catch(() => ({})); alert(e.error || 'فشل جلب التحليلات'); return; }
            const data = await resp.json();
            setAnalytics({
                summary: data.summary || analytics.summary,
                topLocations: data.topLocations || [],
                monthlyTrends: data.monthlyTrends || [],
                distributions: {
                    agency: data.distributions?.agency || [],
                    jobTitle: data.distributions?.jobTitle || [],
                    administration: data.distributions?.administration || [],
                    gender: data.distributions?.gender || [],
                    city: data.distributions?.city || []
                }
            });
        } catch { alert('فشل الاتصال بالخادم أثناء جلب التحليلات'); }
        finally { setAnalyticsLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminIdentity]);

    /* ---------- Create user ---------- */
    const handleCreateUser = async (e) => {
        e.preventDefault();
        setMessage('');
        try {
            const resp = await fetch(API_ENDPOINTS.ADMIN_CREATE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-employee-identity': adminIdentity },
                body: JSON.stringify(newUser)
            });
            const data = await resp.json();
            if (!resp.ok) { setMessage(data.error || 'فشل إنشاء المستخدم'); return; }
            setMessage('✅ تم إنشاء الحساب بنجاح');
            setNewUser({ identity_number: '', full_name: '', phone_number: '', location: '', role: 'employee' });
            setQ('');
            setExactDate('');
            await fetchUsers({ page: 1, search: '', date: '' });
            setView('list');
        } catch { setMessage('خطأ في الاتصال بالخادم'); }
    };

    const handleSearch = (e) => { e.preventDefault(); fetchUsers({ page: 1, search: q.trim(), date: exactDate, status: filterStatus }); };
    const handleLogout = () => { sessionStorage.removeItem('adminView'); sessionStorage.removeItem('employee'); navigate('/'); };
    const goToPage = (n) => { if (n < 1 || n > totalPages || n === page) return; fetchUsers({ page: n, search: q.trim(), date: exactDate, status: filterStatus }); };

    const handleDeleteUser = async () => {
        if (!deleteModal) return;
        try {
            const resp = await fetch(API_ENDPOINTS.ADMIN_DELETE_USER(deleteModal.identityNumber), {
                method: 'DELETE',
                headers: { 'x-employee-identity': adminIdentity }
            });
            if (resp.ok) {
                setDeleteModal(null);
                fetchUsers({ page, search: q, date: exactDate, status: filterStatus });
                if (view === 'analytics') fetchAnalytics();
            } else {
                const data = await resp.json();
                alert(data.error || 'فشل حذف الموظف');
            }
        } catch (e) { alert('خطأ في الاتصال بالخادم'); }
    };

    /* Live search */
    useEffect(() => {
        if (view !== 'list' || !adminIdentity) return;
        if (!didInitLiveSearch.current) { didInitLiveSearch.current = true; return; }
        const t = setTimeout(() => fetchUsers({ page: 1, search: q.trim(), date: exactDate, status: filterStatus }), 320);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, exactDate, filterStatus, view, adminIdentity]);

    const switchView = (v) => {
        setView(v);
        sessionStorage.setItem('adminView', v);
        setSidebarOpen(false);
        if (v === 'list' && users.length === 0 && !loading) fetchUsers({ page: 1, search: q.trim() });
        if (v === 'analytics') fetchAnalytics();
    };

    const rowsRange = useMemo(() => {
        if (totalUsers === 0) return '0 – 0';
        return `${(page - 1) * PAGE_SIZE + 1} – ${Math.min(page * PAGE_SIZE, totalUsers)}`;
    }, [page, totalUsers]);

    /* ---- Chart data helpers ---- */
    const genderData = analytics.distributions.gender.map(d => ({ name: d.label, value: d.count }));
    const PIE_COLORS = ['#0f4e2b', '#16a34a', '#86efac', '#f59e0b', '#bbf7d0', '#fbbf24'];

    /* ---- Selection Logic ---- */
    const toggleUserSelect = (id) => {
        const next = new Set(selectedUsers);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedUsers(next);
    };

    const toggleSelectAll = () => {
        if (selectedUsers.size === users.length) setSelectedUsers(new Set());
        else {
            const next = new Set();
            users.forEach(u => next.add(u.identity_number));
            setSelectedUsers(next);
        }
    };

    /* ---- Completion pie ---- */
    const completionPieData = [
        { name: 'مكتمل', value: analytics.summary.employeesFilled },
        { name: 'غير مكتمل', value: analytics.summary.employeesPending }
    ];

    /* ---- Render distribution bars ---- */
    const renderDistribution = (title, items, fillClass, icon) => {
        const max = items.length ? Math.max(...items.map(i => Number(i.count || 0)), 1) : 1;
        return (
            <div className="analytics-panel">
                <h4><span className="panel-icon">{icon}</span>{title}</h4>
                <div className="bars-list">
                    {items.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>لا توجد بيانات.</p>}
                    {items.map(item => {
                        const v = Number(item.count || 0);
                        const w = Math.max(4, Math.round((v / max) * 100));
                        return (
                            <div className="bar-row" key={item.label}>
                                <div className="bar-head">
                                    <span>{item.label}</span>
                                    <strong>{v}</strong>
                                </div>
                                <div className="bar-track">
                                    <div className={`bar-fill ${fillClass}`} style={{ width: `${w}%` }} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    /* ---- Export Handlers ---- */
    const fetchExportData = async () => {
        try {
            const params = new URLSearchParams();
            if (q.trim()) params.set('search', q.trim());
            if (exactDate) params.set('exactDate', exactDate);
            if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);

            const resp = await fetch(`${API_ENDPOINTS.ADMIN_USERS.replace('/users', '/export')}?${params}`, {
                headers: { 'Content-Type': 'application/json', 'x-employee-identity': adminIdentity }
            });
            if (!resp.ok) throw new Error('فشل جلب بيانات التصدير');
            const data = await resp.json();
            return data.results || [];
        } catch (err) {
            console.error(err);
            alert('حدث خطأ أثناء تحميل البيانات للتصدير.');
            return null;
        }
    };

    const exportToPDF = async () => {
        const rawData = await fetchExportData();
        if (!rawData || rawData.length === 0) return alert('لا توجد بيانات للتصدير.');
        const data = selectedUsers.size > 0 ? rawData.filter(u => selectedUsers.has(u.identity_number)) : rawData;

        const printWindow = window.open('', '_blank');
        const tableHtml = `
            <html dir="rtl" lang="ar">
            <head>
                <title>تقرير الموظفين</title>
                <style>
                    body { font-family: 'Tahoma', 'Arial', sans-serif; padding: 20px; color: #111; }
                    h1 { text-align: center; color: #0f4e2b; margin-bottom: 20px; border-bottom: 2px solid #0f4e2b; padding-bottom: 10px; }
                    table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 13px; }
                    th, td { border: 1px solid #ddd; padding: 10px; text-align: right; }
                    th { background-color: #f0fdf4; color: #0f4e2b; font-weight: bold; }
                    tr:nth-child(even) { background-color: #fafafa; }
                </style>
            </head>
            <body>
                <h1>رئاسة الشؤون الدينية بالمسجد الحرام والمسجد النبوي</h1>
                <h3>تقرير بيانات الموظفين وحالة النماذج</h3>
                <table>
                    <thead>
                        <tr>
                            <th>رقم الهوية</th>
                            <th>الاسم الكامل</th>
                            <th>الجوال</th>
                            <th>الوكالة</th>
                            <th>الإدارة</th>
                            <th>المسمى الوظيفي</th>
                            <th>حالة النموذج</th>
                            <th>تاريخ التعبئة</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${data.map(u => `
                            <tr>
                                <td dir="ltr">${u.identity_number}</td>
                                <td>${u.full_name}</td>
                                <td dir="ltr">${u.phone_number || '—'}</td>
                                <td>${u.agency || '—'}</td>
                                <td>${u.administration || '—'}</td>
                                <td>${u.job_title || '—'}</td>
                                <td>${u.formulaire_id ? 'مكتمل' : 'بانتظار التعبئة'}</td>
                                <td>${u.filled_date ? new Date(u.filled_date).toLocaleDateString() : '—'}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
                <script>
                    window.onload = function() { window.print(); window.close(); }
                </script>
            </body>
            </html>
        `;
        printWindow.document.write(tableHtml);
        printWindow.document.close();
    };

    const exportToExcel = async () => {
        const rawData = await fetchExportData();
        if (!rawData || rawData.length === 0) return alert('لا توجد بيانات للتصدير.');
        const data = selectedUsers.size > 0 ? rawData.filter(u => selectedUsers.has(u.identity_number)) : rawData;

        const wsData = data.map(u => ({
            'رقم الهوية': u.identity_number,
            'الاسم الكامل': u.full_name,
            'رقم الجوال': u.phone_number,
            'الموقع': u.location,
            'الوكالة': u.agency || '—',
            'الإدارة': u.administration || '—',
            'المسمى الوظيفي': u.job_title || '—',
            'الجنس': u.gender === 'male' ? 'ذكر' : (u.gender === 'female' ? 'أنثى' : '—'),
            'الحالة الاجتماعية': u.marital_status || '—',
            'الدور': u.role === 'admin' ? 'مشرف' : 'موظف',
            'عدد التابعين': u.dependents_count || (u.dependents ? u.dependents.length : 0),
            'حالة النموذج': u.formulaire_id ? 'مكتمل' : 'غير مكتمل',
            'تاريخ التعبئة': u.filled_date ? new Date(u.filled_date).toLocaleDateString() : '—',
        }));
        const ws = XLSX.utils.json_to_sheet(wsData);
        // Set column width to fit content ideally
        ws['!cols'] = [
            { wch: 15 }, { wch: 30 }, { wch: 15 }, { wch: 20 },
            { wch: 20 }, { wch: 20 }, { wch: 25 }, { wch: 10 },
            { wch: 15 }, { wch: 10 }, { wch: 12 }, { wch: 15 }, { wch: 15 }
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Employees");
        XLSX.writeFile(wb, "employees_report.xlsx");
    };

    const exportToSQL = async () => {
        const rawData = await fetchExportData();
        if (!rawData || rawData.length === 0) return alert('لا توجد بيانات للتصدير.');
        const data = selectedUsers.size > 0 ? rawData.filter(u => selectedUsers.has(u.identity_number)) : rawData;

        const fields = [
            'identity_number', 'full_name', 'phone_number', 'location', 'role',
            'identity_expiry_date', 'birth_date', 'agency', 'administration',
            'job_title', 'gender', 'marital_status'
        ];

        const escape = (val) => val ? `'${String(val).replace(/'/g, "''")}'` : 'NULL';

        let sqlStr = `INSERT INTO employees (${fields.join(', ')}) VALUES \n`;
        const rows = data.map(u => {
            const values = [
                escape(u.identity_number),
                escape(u.full_name),
                escape(u.phone_number),
                escape(u.location),
                escape(u.role),
                u.identity_expiry_date ? `'${new Date(u.identity_expiry_date).toISOString().split('T')[0]}'` : 'NULL',
                u.birth_date ? `'${new Date(u.birth_date).toISOString().split('T')[0]}'` : 'NULL',
                escape(u.agency),
                escape(u.administration),
                escape(u.job_title),
                escape(u.gender || 'male'),
                escape(u.marital_status || 'single')
            ];
            return `(${values.join(', ')})`;
        });

        sqlStr += rows.join(",\n") + ";";
        const blob = new Blob([sqlStr], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "employees_export.sql";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="admin-shell">
            {/* ===== SIDEBAR ===== */}
            <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
                <div className="sidebar-brand">
                    <div className="logo">
                        <img src="/images/logo white.png" alt="logo" />                </div>

                    <span className="sidebar-brand-title">لوحة الإدارة</span>
                    <span className="sidebar-brand-sub">نظام التأمين الصحي</span>
                </div>
                <nav>
                    <ul>
                        <li className={`nav-item ${view === 'analytics' ? 'active' : ''}`} onClick={() => switchView('analytics')}>
                            الإحصائيات والتحليلات
                        </li>
                        <li className={`nav-item ${view === 'list' ? 'active' : ''}`} onClick={() => switchView('list')}>
                            بيانات الموظفين
                        </li>
                        <li className={`nav-item ${view === 'create' ? 'active' : ''}`} onClick={() => switchView('create')}>
                            إضافة موظف جديد
                        </li>
                    </ul>
                </nav>
                <div className="sidebar-footer">
                    <div className="sidebar-admin-card">
                        <div className="sidebar-admin-avatar">م</div>
                        <div className="sidebar-admin-info">
                            <span className="sidebar-admin-label">مشرف النظام</span>
                            <span className="sidebar-admin-id">{adminIdentity}</span>
                        </div>
                    </div>
                </div>
            </aside>

            <div className={`sidebar-overlay ${sidebarOpen ? 'show' : ''}`} onClick={() => setSidebarOpen(false)} />

            {/* ===== MAIN ===== */}
            <main className="admin-main">
                {/* Header */}
                <header className="admin-main-header">
                    <button className="menu-btn" type="button" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
                    <h2>
                        {view === 'analytics' && 'الإحصائيات والتحليلات'}
                        {view === 'list' && 'بيانات الموظفين'}
                        {view === 'create' && 'إنشاء حساب جديد'}
                    </h2>
                    <div className="header-actions">

                        <button className="danger-btn" onClick={handleLogout}>تسجيل الخروج</button>
                    </div>
                </header>

                {/* Content */}
                <div className="admin-content">

                    {/* ===== LIST VIEW ===== */}
                    {view === 'list' && (
                        <div className="fade-in">
                            <div className="stats-row">
                                <KPICard label="إجمالي الموظفين" value={summary.employees || 0}
                                    icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>}
                                    accentColor="#0f4e2b" iconBg="#f0fdf4" />
                                <KPICard label="نماذج مكتملة" value={summary.employeesFilled || 0}
                                    icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>}
                                    accentColor="#16a34a" iconBg="#dcfce7" />
                                <KPICard label="بانتظار التعبئة" value={summary.employeesPending || 0}
                                    icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>}
                                    accentColor="#f59e0b" iconBg="#fef3c7" />
                            </div>

                            <form onSubmit={handleSearch} className="admin-search">
                                <div className="search-filters">
                                    <div className="search-input-wrap" style={{ flex: 1 }}>
                                        <span className="search-input-icon">🔍</span>
                                        <input
                                            placeholder="ابحث بالاسم أو رقم الهوية أو رقم الجوال..."
                                            value={q}
                                            onChange={(e) => setQ(e.target.value)}
                                        />
                                    </div>
                                    <input
                                        type="date"
                                        className="search-date-picker"
                                        value={exactDate}
                                        onChange={(e) => setExactDate(e.target.value)}
                                        onClick={(e) => {
                                            try {
                                                if (typeof e.target.showPicker === 'function') {
                                                    e.target.showPicker();
                                                }
                                            } catch (err) { }
                                        }}
                                        title="تاريخ التعبئة"
                                    />
                                    <select
                                        className="search-date-picker"
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                        title="حالة النموذج"
                                        style={{ minWidth: '120px' }}
                                    >
                                        <option value="all">الكل (الحالة)</option>
                                        <option value="filled">نماذج مكتملة</option>
                                        <option value="pending">بانتظار التعبئة</option>
                                    </select>
                                    <button type="submit" disabled={loading} className="primary-btn">
                                        {loading ? '⏳' : 'بحث'}
                                    </button>
                                    <button type="button" className="ghost-btn" onClick={() => { setQ(''); setExactDate(''); setFilterStatus('all'); fetchUsers({ page: 1, search: '', date: '', status: 'all' }); }}>
                                        إلغاء التصفية
                                    </button>
                                </div>
                                <div className="export-actions">
                                    <button type="button" className="ghost-btn export-btn" onClick={exportToPDF}>📄تصدير PDF</button>
                                    <button type="button" className="ghost-btn export-btn" onClick={exportToExcel}>📊تصدير Excel</button>
                                    <button type="button" className="ghost-btn export-btn" onClick={exportToSQL}>💾تصدير SQL</button>
                                </div>
                            </form>

                            <div className={`admin-list ${compactRows ? 'compact' : ''}`}>
                                <table>
                                    <thead>
                                        <tr>
                                            <th style={{ width: 40, textAlign: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={users.length > 0 && selectedUsers.size === users.length}
                                                    onChange={toggleSelectAll}
                                                />
                                            </th>
                                            <th>رقم الهوية</th>
                                            <th>الاسم الكامل</th>
                                            <th>رقم الجوال</th>
                                            <th>الموقع</th>
                                            <th>الدور</th>
                                            <th>التاريخ</th>
                                            <th>إجراء</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading && (
                                            <tr><td colSpan="6">
                                                <div className="table-empty-state">
                                                    <span className="table-empty-icon">⏳</span>
                                                    <p className="table-empty-text">جاري تحميل البيانات...</p>
                                                </div>
                                            </td></tr>
                                        )}
                                        {!loading && users.length === 0 && (
                                            <tr><td colSpan="6">
                                                <div className="table-empty-state">
                                                    <span className="table-empty-icon">🔍</span>
                                                    <p className="table-empty-text">لا توجد نتائج مطابقة</p>
                                                </div>
                                            </td></tr>
                                        )}
                                        {users.map((u) => (
                                            <tr key={u.identity_number}>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedUsers.has(u.identity_number)}
                                                        onChange={() => toggleUserSelect(u.identity_number)}
                                                    />
                                                </td>
                                                <td style={{ fontFamily: 'monospace', letterSpacing: '0.05em' }}>{u.identity_number}</td>
                                                <td style={{ fontWeight: 600 }}>{u.full_name}</td>
                                                <td style={{ direction: 'ltr' }}>{u.phone_number || '—'}</td>
                                                <td>{u.location || '—'}</td>
                                                <td>
                                                    <span className={`role-badge ${u.role === 'admin' ? 'admin' : 'employee'}`}>
                                                        {u.role === 'admin' ? 'مشرف' : 'موظف'}
                                                    </span>
                                                </td>
                                                <td style={{ color: 'var(--text-muted)' }}>
                                                    {u.filled_date ? new Date(u.filled_date).toLocaleDateString() : '—'}
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className={`table-action-btn ${u.formulaire_id ? 'filled' : 'not-filled'}`}
                                                        onClick={() => navigate('/form', { state: { identityNumber: u.identity_number, employee: u } })}
                                                    >
                                                        {u.formulaire_id ? 'مكتمل' : 'النموذج'}
                                                    </button>
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                                        <button
                                                            type="button"
                                                            className="table-delete-btn"
                                                            onClick={() => {
                                                                const url = `${API_ENDPOINTS.BASE_URL}/api/formulaire/${u.identity_number}/pdf`;
                                                                window.open(url, '_blank');
                                                            }}
                                                            title="تصدير PDF"
                                                            style={{ color: '#0f4e2b' }}
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="table-delete-btn"
                                                            onClick={() => setDeleteModal({ identityNumber: u.identity_number, name: u.full_name })}
                                                            title="حذف الموظف"
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="pagination-wrap">
                                <div className="pagination-info">
                                    عرض <strong>{rowsRange}</strong> من <strong>{totalUsers}</strong> سجل
                                </div>
                                <div className="pagination-controls">
                                    <button className="ghost-btn" disabled={page <= 1 || loading} onClick={() => goToPage(page - 1)}>← السابق</button>
                                    <span className="page-indicator">{page} / {totalPages}</span>
                                    <button className="ghost-btn" disabled={page >= totalPages || loading} onClick={() => goToPage(page + 1)}>التالي →</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ===== ANALYTICS VIEW ===== */}
                    {view === 'analytics' && (
                        <div className="analytics fade-in">
                            <div className="analytics-header">
                                <h3>مؤشرات الأداء والتحليلات</h3>
                                <button className="analytics-refresh-btn" onClick={fetchAnalytics} disabled={analyticsLoading}>
                                    تحديث
                                </button>
                            </div>

                            {analyticsLoading ? (
                                <div className="analytics-loading">
                                    <div className="analytics-spinner" />
                                    <p>جاري تحميل لوحة التحليلات...</p>
                                </div>
                            ) : (
                                <>
                                    {/* KPI Row */}
                                    <div className="analytics-kpis">
                                        <KPICard label="إجمالي الموظفين" value={analytics.summary.employeesTotal || 0}
                                            icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>}
                                            accentColor="#0f4e2b" iconBg="#f0fdf4"
                                            sub={`${analytics.summary.adminsTotal || 0} مشرف`} />
                                        <KPICard label="معدل الاكتمال" value={`${analytics.summary.completionRate || 0}%`}
                                            icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>}
                                            accentColor="#16a34a" iconBg="#dcfce7"
                                            sub={`${analytics.summary.employeesFilled || 0} مكتمل`} />
                                        <KPICard label="إجمالي التابعين" value={analytics.summary.totalDependents || 0}
                                            icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>}
                                            accentColor="#f59e0b" iconBg="#fef3c7"
                                            sub={`متوسط ${analytics.summary.avgDependentsPerFilled || 0} / موظف`} />
                                        <KPICard label="متوسط العمر" value={analytics.summary.avgAge || 0}
                                            icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg>}
                                            accentColor="#0f4e2b" iconBg="#f0fdf4"
                                            sub="سنة" />
                                    </div>

                                    <div className="analytics-grid">

                                        {/* ---- Monthly Trends Area Chart ---- */}
                                        <div className="analytics-panel analytics-panel-wide">
                                            <h4>اتجاه النمو الشهري</h4>
                                            {analytics.monthlyTrends.length === 0 ? (
                                                <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>لا توجد بيانات شهرية.</p>
                                            ) : (
                                                <ResponsiveContainer width="100%" height={240}>
                                                    <AreaChart data={analytics.monthlyTrends} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                                                        <defs>
                                                            <linearGradient id="empGrad" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor="#0f4e2b" stopOpacity={0.3} />
                                                                <stop offset="95%" stopColor="#0f4e2b" stopOpacity={0} />
                                                            </linearGradient>
                                                            <linearGradient id="formGrad" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                                                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                                            </linearGradient>
                                                        </defs>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                                                        <XAxis dataKey="month" tick={{ fill: '#8b949e', fontSize: 11 }} />
                                                        <YAxis tick={{ fill: '#8b949e', fontSize: 11 }} />
                                                        <Tooltip content={<ChartTooltip />} />
                                                        <Legend wrapperStyle={{ color: '#8b949e', fontSize: '0.82rem' }} />
                                                        <Area type="monotone" dataKey="employees" name="موظفون" stroke="#0f4e2b" strokeWidth={2} fill="url(#empGrad)" dot={{ fill: '#0f4e2b', r: 3 }} />
                                                        <Area type="monotone" dataKey="forms" name="نماذج" stroke="#3b82f6" strokeWidth={2} fill="url(#formGrad)" dot={{ fill: '#3b82f6', r: 3 }} />
                                                    </AreaChart>
                                                </ResponsiveContainer>
                                            )}
                                        </div>

                                        {/* ---- Completion Rate Donut ---- */}
                                        <div className="analytics-panel">
                                            <h4>نسبة اكتمال النماذج</h4>
                                            <div className="completion-rate-big">{analytics.summary.completionRate || 0}%</div>
                                            <div className="completion-track">
                                                <div className="completion-fill"
                                                    style={{ width: `${Math.min(100, analytics.summary.completionRate || 0)}%` }} />
                                            </div>
                                            <div className="completion-meta">
                                                <span>✅ مكتمل: {analytics.summary.employeesFilled || 0}</span>
                                                <span>⏳ غير مكتمل: {analytics.summary.employeesPending || 0}</span>
                                            </div>
                                            {(analytics.summary.employeesFilled + analytics.summary.employeesPending) > 0 && (
                                                <ResponsiveContainer width="100%" height={160}>
                                                    <PieChart>
                                                        <Pie
                                                            data={completionPieData}
                                                            cx="50%" cy="50%"
                                                            innerRadius={48} outerRadius={72}
                                                            paddingAngle={3}
                                                            dataKey="value"
                                                        >
                                                            {completionPieData.map((_, i) => (
                                                                <Cell key={i} fill={i === 0 ? '#22c55e' : '#374151'} stroke="none" />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip content={<ChartTooltip />} />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            )}
                                        </div>

                                        {/* ---- Gender Donut ---- */}
                                        <div className="analytics-panel">
                                            <h4>توزيع الجنس</h4>
                                            {genderData.length === 0 ? (
                                                <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>لا توجد بيانات.</p>
                                            ) : (
                                                <>
                                                    <ResponsiveContainer width="100%" height={180}>
                                                        <PieChart>
                                                            <Pie data={genderData} cx="50%" cy="50%"
                                                                innerRadius={55} outerRadius={80}
                                                                paddingAngle={3} dataKey="value"
                                                            >
                                                                {genderData.map((_, i) => (
                                                                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="none" />
                                                                ))}
                                                            </Pie>
                                                            <Tooltip content={<ChartTooltip />} />
                                                            <Legend wrapperStyle={{ color: '#8b949e', fontSize: '0.82rem' }} />
                                                        </PieChart>
                                                    </ResponsiveContainer>
                                                    <div className="bars-list" style={{ marginTop: 8 }}>
                                                        {genderData.map((d, i) => (
                                                            <div className="bar-row" key={d.name}>
                                                                <div className="bar-head">
                                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: PIE_COLORS[i % PIE_COLORS.length], display: 'inline-block' }} />
                                                                        {d.name}
                                                                    </span>
                                                                    <strong>{d.value}</strong>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        {/* ---- Location Bar Chart ---- */}
                                        <div className="analytics-panel analytics-panel-wide">
                                            <h4>التوزيع حسب الموقع</h4>
                                            {analytics.topLocations.length === 0 ? (
                                                <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>لا توجد بيانات مواقع.</p>
                                            ) : (
                                                <ResponsiveContainer width="100%" height={200}>
                                                    <BarChart data={analytics.topLocations} layout="vertical" margin={{ top: 0, right: 10, left: 60, bottom: 0 }}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                                                        <XAxis type="number" tick={{ fill: '#8b949e', fontSize: 11 }} />
                                                        <YAxis dataKey="location" type="category" tick={{ fill: '#e6edf3', fontSize: 12 }} width={54} />
                                                        <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                                                        <Bar dataKey="employees" name="موظفون" fill="#0f4e2b" radius={[0, 6, 6, 0]}>
                                                            {analytics.topLocations.map((_, i) => (
                                                                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                                            ))}
                                                        </Bar>
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            )}
                                        </div>

                                        {/* ---- Distribution Panels ---- */}
                                        {renderDistribution('توزيع الوكالات', analytics.distributions.agency, 'dist-agency', '')}
                                        {renderDistribution('توزيع المسميات الوظيفية', analytics.distributions.jobTitle, 'dist-job', '')}
                                        {renderDistribution('توزيع الإدارات', analytics.distributions.administration, 'dist-admin', '')}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* ===== CREATE VIEW ===== */}
                    {view === 'create' && (
                        <div className="create-user fade-in">
                            <form onSubmit={handleCreateUser} className="create-form">
                                <div className="create-form-title">إنشاء حساب موظف جديد</div>

                                <label>رقم الهوية *</label>
                                <input
                                    value={newUser.identity_number}
                                    onChange={e => setNewUser({ ...newUser, identity_number: e.target.value })}
                                    placeholder="مثال: 1234567890"
                                    required
                                />

                                <label>الاسم الكامل *</label>
                                <input
                                    value={newUser.full_name}
                                    onChange={e => setNewUser({ ...newUser, full_name: e.target.value })}
                                    placeholder="الاسم الرباعي"
                                    required
                                />

                                <label>رقم الجوال</label>
                                <input
                                    value={newUser.phone_number}
                                    onChange={e => setNewUser({ ...newUser, phone_number: e.target.value })}
                                    placeholder="05xxxxxxxx"
                                />

                                <label>الموقع / المنطقة</label>
                                <input
                                    value={newUser.location}
                                    onChange={e => setNewUser({ ...newUser, location: e.target.value })}
                                    placeholder="مثال: مكة المكرمة"
                                />

                                <label>الدور الوظيفي</label>
                                <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })}>
                                    <option value="employee">🧑‍💼 موظف</option>
                                    <option value="admin">⭐ مشرف</option>
                                </select>

                                <div className="form-actions">
                                    <button type="submit" className="primary-btn">إنشاء الحساب</button>
                                    <button type="button" className="ghost-btn" onClick={() => setView('list')}>إلغاء</button>
                                </div>
                            </form>
                            {message && <div className="create-message">{message}</div>}
                        </div>
                    )}

                    {/* ===== DELETE CONFIRMATION MODAL ===== */}
                    {deleteModal && (
                        <div className="modal-overlay">
                            <div className="modal-content delete-modal">
                                <div className="delete-modal-icon">
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path></svg>
                                </div>
                                <h3>تأكيد الحذف</h3>
                                <p>هل أنت متأكد من حذف الموظف <strong>{deleteModal.name}</strong>؟<br />سيتم حذف جميع بيانات النموذج والتابعين المرتبطة به بشكل نهائي ولا يمكن التراجع عن هذه الخطوة.</p>
                                <div className="modal-actions">
                                    <button onClick={() => setDeleteModal(null)} className="ghost-btn">إلغاء</button>
                                    <button onClick={handleDeleteUser} className="danger-btn">حذف نهائياً</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}

export default Admin;
